import React, { useState, useRef } from 'react';
import { SurveyData, User, DynamicQuestion, SurveySection } from '../types';
import { saveSurvey, getNextSerialNo, convertGoogleDriveUrl } from '../services/storage';
import { Upload, X, Check, Loader, AlertCircle, FileSpreadsheet, ArrowRight, HelpCircle, RefreshCw } from 'lucide-react';
import { useToast } from './Toast';

interface DataImporterProps {
  user: User;
  questions: DynamicQuestion[];
  sections: SurveySection[];
  onSuccess: () => void;
  onClose: () => void;
}

const generateUniqueId = () => {
  return Date.now().toString() + Math.random().toString(36).substring(2, 9);
};

// Robust RFC 4180-compliant CSV parser
function parseCSV(text: string): string[][] {
  const lines: string[][] = [];
  let row: string[] = [];
  let inQuotes = false;
  let currentVal = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          currentVal += '"';
          i++; // Skip next quote
        } else {
          inQuotes = false;
        }
      } else {
        currentVal += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        row.push(currentVal.trim());
        currentVal = '';
      } else if (char === '\r' || char === '\n') {
        row.push(currentVal.trim());
        currentVal = '';
        if (row.some(val => val !== '')) {
          lines.push(row);
        }
        row = [];
        if (char === '\r' && nextChar === '\n') {
          i++; // Skip \n
        }
      } else {
        currentVal += char;
      }
    }
  }

  if (currentVal !== '' || row.length > 0) {
    row.push(currentVal.trim());
    if (row.some(val => val !== '')) {
      lines.push(row);
    }
  }

  return lines;
}

export const DataImporter: React.FC<DataImporterProps> = ({ user, questions, sections, onSuccess, onClose }) => {
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  
  // Step workflow state: 'upload' | 'mapping' | 'preview' | 'importing' | 'completed'
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'completed'>('upload');
  
  // Parsing states
  const [headers, setHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  
  // Execution status
  const [totalRecords, setTotalRecords] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [importLogs, setImportLogs] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Auto-matching algorithm
  const autoMatchHeaders = (csvHeaders: string[]) => {
    const mapping: Record<string, string> = {};
    csvHeaders.forEach(header => {
      const hLower = header.toLowerCase().replace(/[^a-z0-9]/g, '');
      const hMalayalam = header.trim();

      // Look for a close match in active questions
      const matchedQ = questions.find(q => {
        const qIdLower = q.id.toLowerCase();
        const qLabelLower = q.label.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        // Exact / close identifier matches
        if (qIdLower === hLower) return true;
        if (qLabelLower.includes(hLower) && hLower.length > 3) return true;
        if (hLower.includes(qLabelLower) && qLabelLower.length > 3) return true;

        // Common synonyms / Malayalam mappings
        if (hMalayalam.includes('പേര്') || hLower.includes('studentname') || hLower.includes('name')) {
          if (q.id === 'studentName' || q.id === '1782312349252') return true;
        }
        if (hMalayalam.includes('രക്ഷിതാവ്') || hLower.includes('parentname') || hLower.includes('guardian') || hLower.includes('fathername')) {
          if (q.id === 'parentName' || q.id === '1782311895371') return true;
        }
        if (hMalayalam.includes('വയസ്സ്') || hLower.includes('age')) {
          if (q.id === 'age') return true;
        }
        if (hMalayalam.includes('ഫോൺ') || hLower.includes('phone') || hLower.includes('mobile')) {
          if (q.id === 'phone' || q.id === '1782311944131' || q.id === '1782311987788' || q.id === '1782312038535') return true;
        }
        if (hMalayalam.includes('ലിംഗ') || hLower.includes('gender')) {
          if (q.id === 'gender' || q.id === '1782312394122') return true;
        }
        if (hMalayalam.includes('വീട്') || hLower.includes('housename') || hLower.includes('house')) {
          if (q.id === 'houseName') return true;
        }
        if (hMalayalam.includes('വാർഡ്') || hLower.includes('ward')) {
          if (q.id === 'wardNo') return true;
        }
        if (hMalayalam.includes('ക്രമനമ്പർ') || hLower.includes('serial')) {
          if (q.id === 'serialNo') return true;
        }
        if (hMalayalam.includes('മെമ്പർഷിപ്പ്') || hLower.includes('membership') || hLower.includes('iicmember')) {
          if (q.id === 'isIICMember' || q.id === '1782312069160') return true;
        }

        return false;
      });

      mapping[header] = matchedQ ? matchedQ.id : ''; // default empty to let user select
    });
    setColumnMapping(mapping);
  };

  const handleFileProcess = (text: string, name: string) => {
    try {
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        addToast("The uploaded file is empty.", "error");
        return;
      }
      
      const fileHeaders = parsed[0].map(h => h.trim());
      const rows = parsed.slice(1);
      
      if (rows.length === 0) {
        addToast("No data rows found in the CSV file.", "error");
        return;
      }

      setFileName(name);
      setHeaders(fileHeaders);
      setParsedRows(rows);
      autoMatchHeaders(fileHeaders);
      setStep('mapping');
    } catch (e: any) {
      console.error(e);
      addToast("Failed to parse CSV file: " + e.message, "error");
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            handleFileProcess(event.target.result as string, file.name);
          }
        };
        reader.readAsText(file);
      } else {
        addToast("Please upload a valid CSV file (.csv)", "error");
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          handleFileProcess(event.target.result as string, file.name);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleMappingChange = (header: string, questionId: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [header]: questionId
    }));
  };

  const handleProceedToPreview = () => {
    // Check if at least one column is mapped
    const hasAtLeastOneMapping = Object.values(columnMapping).some(val => val !== '');
    if (!hasAtLeastOneMapping) {
      addToast("Please map at least one column to a question field to proceed.", "error");
      return;
    }
    setStep('preview');
  };

  const handleExecuteImport = async () => {
    setIsProcessing(true);
    setStep('importing');
    setTotalRecords(parsedRows.length);
    setImportedCount(0);
    setImportLogs([]);

    try {
      // 1. Determine starting serial number sequential counter
      const startSerialStr = await getNextSerialNo();
      let startNo = parseInt(startSerialStr, 10);
      if (isNaN(startNo)) startNo = 1;

      // Filter sections once for quick checks
      const isRepeaterSecMap = sections.reduce<Record<string, boolean>>((acc, s) => ({
        ...acc,
        [s.id]: !!s.isRepeater
      }), {});

      // Helper to process yes/no fields correctly
      const cleanYesNo = (val: string) => {
        const cleanVal = val.toLowerCase().trim();
        if (['yes', 'true', 'y', '1', 'ശരി', 'ഉണ്ട്', 'അതെ'].includes(cleanVal)) return true;
        if (['no', 'false', 'n', '0', 'അല്ല', 'ഇല്ല'].includes(cleanVal)) return false;
        return val;
      };

      // Import sequentially to avoid parallel transaction conflicts on metadata document
      let count = 0;

      for (let idx = 0; idx < parsedRows.length; idx++) {
        const row = parsedRows[idx];
        const uniqueId = generateUniqueId();
        const generatedSerialNo = (startNo + idx).toString().padStart(4, '0');

        // Initialize nested dynamic structure matching App's state
        const initialSectionsData: Record<string, any> = {};
        
        // Seed repeaters if any
        sections.forEach(sec => {
          if (sec.isRepeater) {
            initialSectionsData[sec.id] = [{ id: generateUniqueId() }];
          } else {
            initialSectionsData[sec.id] = {};
          }
        });

        // Always seed serialNo inside sectionA as standard fallback
        if (!initialSectionsData['sectionA']) {
          initialSectionsData['sectionA'] = {};
        }
        initialSectionsData['sectionA'].serialNo = generatedSerialNo;

        // Map CSV cells to the correct section nested keys
        headers.forEach((header, colIdx) => {
          const qId = columnMapping[header];
          if (!qId) return; // skipped column

          const cellVal = row[colIdx];
          if (cellVal === undefined || cellVal === null || cellVal.trim() === '') return;

          // Find question configuration to check type and section
          const q = questions.find(question => question.id === qId);
          if (!q) return;

          const targetSection = q.section || 'sectionA';
          let finalVal: any = cellVal.trim();

          // Type conversions
          if (q.type === 'number') {
            const num = parseFloat(finalVal);
            if (!isNaN(num)) finalVal = num;
          } else if (q.type === 'yesno') {
            finalVal = cleanYesNo(finalVal);
          } else if (q.type === 'image') {
            finalVal = convertGoogleDriveUrl(finalVal);
          }

          // Assign to nested structure
          if (isRepeaterSecMap[targetSection]) {
            // Populate first element of array
            if (!Array.isArray(initialSectionsData[targetSection])) {
              initialSectionsData[targetSection] = [{ id: generateUniqueId() }];
            }
            initialSectionsData[targetSection][0][qId] = finalVal;
          } else {
            initialSectionsData[targetSection] = initialSectionsData[targetSection] || {};
            initialSectionsData[targetSection][qId] = finalVal;
          }
        });

        // Ensure all membership fields are synchronized matching exactly how PaymentsManager and Dashboard expect
        let isMember = false;
        
        const checkMemberVal = (val: any): boolean => {
          if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && (val.toLowerCase() === 'yes' || val.toLowerCase() === 'true'))) {
            return true;
          }
          return false;
        };

        // Check root keys (if any)
        const keysToCheck = ['isIICMember', '1782312069160', 'iicMember', 'isMember', 'membership'];
        keysToCheck.forEach(k => {
          if (checkMemberVal(initialSectionsData[k])) isMember = true;
          if (initialSectionsData.sectionA && checkMemberVal(initialSectionsData.sectionA[k])) isMember = true;
          if (initialSectionsData.section_1782311875584 && checkMemberVal(initialSectionsData.section_1782311875584[k])) isMember = true;
        });

        // If isMember is detected, or if one of these keys exists in initialSectionsData (meaning it was mapped but might be false)
        const isMembershipMapped = headers.some(h => {
          const qId = columnMapping[h];
          return qId === 'isIICMember' || qId === '1782312069160';
        });

        if (isMembershipMapped) {
          const statusStr = isMember ? 'Yes' : 'No';
          const statusBool = isMember;

          const priorityKeys = ['isIICMember', 'iicMember', 'membership', 'isMember', 'iic_member', 'memberStatus', '1782312069160'];
          
          // Seed all structures
          priorityKeys.forEach(key => {
            const valToStore = (key === 'iicMember' || key === '1782312069160') ? statusStr : statusBool;
            
            // sectionA
            initialSectionsData.sectionA = initialSectionsData.sectionA || {};
            initialSectionsData.sectionA[key] = valToStore;

            // section_1782311875584
            initialSectionsData.section_1782311875584 = initialSectionsData.section_1782311875584 || {};
            initialSectionsData.section_1782311875584[key] = valToStore;
          });
        }

        // Construct fully-formed SurveyData object matching App expectations
        const surveyData: SurveyData = {
          id: uniqueId,
          submittedBy: user.id,
          submittedAt: new Date().toISOString(),
          paymentStatus: 'Pending',
          isImported: true,
          ...initialSectionsData
        };

        if (isMembershipMapped) {
          const statusStr = isMember ? 'Yes' : 'No';
          const statusBool = isMember;
          const priorityKeys = ['isIICMember', 'iicMember', 'membership', 'isMember', 'iic_member', 'memberStatus', '1782312069160'];
          priorityKeys.forEach(key => {
            surveyData[key] = (key === 'iicMember' || key === '1782312069160') ? statusStr : statusBool;
          });
        }

        // If a studentName is not resolved, fallback to the first row column or placeholder
        const finalStudentName = surveyData.sectionA?.studentName || 'Student Name';
        
        // Attempt firestore write sequentially
        try {
          await saveSurvey(surveyData);
          count++;
          setImportedCount(count);
        } catch (writeErr: any) {
          console.error(`Failed to write row ${idx + 1}:`, writeErr);
          setImportLogs(prev => [...prev, `Row ${idx + 1} (${finalStudentName}): Failed to save.`]);
        }
      }

      setStep('completed');
      addToast(`Successfully imported ${count} registrations!`, "success");
    } catch (err: any) {
      console.error(err);
      addToast("Failed executing import bulk action: " + err.message, "error");
      setStep('mapping');
    } finally {
      setIsProcessing(false);
    }
  };

  const getMappedQuestionLabel = (qId: string) => {
    const q = questions.find(question => question.id === qId);
    if (!q) return '';
    const s = sections.find(sec => sec.id === q.section);
    return `${s ? s.label.split(':')[0] : 'General'}: ${q.label}`;
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-150">
        
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 bg-emerald-700 text-white">
          <div className="flex items-center space-x-3">
            <FileSpreadsheet size={24} />
            <div>
              <h3 className="font-bold text-lg leading-tight">Import Bulk Data</h3>
              <p className="text-emerald-100 text-xs mt-0.5">Upload a CSV file and map columns to dynamic survey questions</p>
            </div>
          </div>
          <button 
            disabled={isProcessing}
            onClick={onClose} 
            className="p-1 rounded-full text-emerald-100 hover:text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body Container */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          
          {/* STEP 1: UPLOAD ZONE */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div 
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${
                  dragActive 
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700 scale-[0.99]' 
                    : 'border-slate-300 hover:border-emerald-500 hover:bg-slate-50 text-slate-500'
                }`}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  accept=".csv" 
                  className="hidden" 
                />
                <Upload size={48} className="text-slate-400 mb-4 animate-bounce" />
                <p className="font-semibold text-gray-700 text-center">Drag and drop your CSV file here</p>
                <p className="text-xs text-slate-400 text-center mt-1">Only .csv spreadsheets are supported</p>
                <button className="mt-4 bg-emerald-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm shadow-sm">
                  Select File from Computer
                </button>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-slate-600 text-xs space-y-2">
                <p className="font-bold text-slate-700 flex items-center gap-1.5">
                  <AlertCircle size={14} className="text-slate-500" /> Tips for a perfect import:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-500">
                  <li>Ensure the first row of your sheet contains descriptive headers (e.g., <i>Student Name</i>, <i>Age</i>, <i>Gender</i>, <i>Phone</i>).</li>
                  <li>Our smart mapper automatically detects and pairs columns containing matching labels.</li>
                  <li>For Yes/No fields, make sure cells contain simple representations like "Yes", "No", "True", or "False".</li>
                </ul>
              </div>
            </div>
          )}

          {/* STEP 2: MAPPING STEP */}
          {step === 'mapping' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">Map CSV Columns to Form Fields</span>
                <span className="text-xs px-2.5 py-1 rounded bg-slate-100 border border-slate-200 text-slate-600 font-medium">
                  {fileName} ({parsedRows.length} rows found)
                </span>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider">
                      <th className="px-4 py-3">CSV Column Header</th>
                      <th className="px-4 py-3">Sample Value (Row 1)</th>
                      <th className="px-4 py-3 w-1/2">Maps to Survey Question</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {headers.map((header) => {
                      const sampleVal = parsedRows[0]?.[headers.indexOf(header)] || '-';
                      const currentMappedId = columnMapping[header] || '';

                      return (
                        <tr key={header} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 font-semibold text-slate-700">{header}</td>
                          <td className="px-4 py-3 text-slate-500 truncate max-w-[200px]" title={sampleVal}>
                            {sampleVal}
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={currentMappedId}
                              onChange={(e) => handleMappingChange(header, e.target.value)}
                              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                            >
                              <option value="">🚫 Do Not Import (Skip Column)</option>
                              {sections.map(sec => {
                                const secQs = questions.filter(q => q.section === sec.id);
                                if (secQs.length === 0) return null;
                                return (
                                  <optgroup key={sec.id} label={sec.label}>
                                    {secQs.map(q => (
                                      <option key={q.id} value={q.id}>
                                        {q.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                );
                              })}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center pt-2">
                <button
                  onClick={() => {
                    setStep('upload');
                    setFileName(null);
                  }}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Change File
                </button>
                <button
                  onClick={handleProceedToPreview}
                  className="bg-emerald-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm shadow flex items-center space-x-1.5"
                >
                  <span>Preview & Verify</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: PREVIEW STEP */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">Final Verification: Data Preview</span>
                <span className="text-xs text-slate-500">Previewing first 3 rows before database commit</span>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-700 text-xs font-bold">
                      <th className="px-4 py-3 w-16">Row</th>
                      {headers.map(h => {
                        const qId = columnMapping[h];
                        if (!qId) return null;
                        return (
                          <th key={h} className="px-4 py-3 min-w-[150px]">
                            <div className="font-bold text-slate-800">{h}</div>
                            <div className="text-[10px] text-emerald-600 font-medium truncate max-w-[150px]">
                              → {getMappedQuestionLabel(qId)}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs text-slate-600">
                    {parsedRows.slice(0, 3).map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-semibold text-slate-400">#{idx + 1}</td>
                        {headers.map(h => {
                          const qId = columnMapping[h];
                          if (!qId) return null;
                          const colIdx = headers.indexOf(h);
                          return (
                            <td key={h} className="px-4 py-3 font-medium text-slate-700 truncate max-w-[200px]">
                              {row[colIdx] || <span className="text-slate-300 italic">Empty</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100 text-emerald-800 text-xs flex items-start gap-2.5">
                <Check size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Ready to Import {parsedRows.length} Registrations</p>
                  <p className="text-emerald-600 mt-1">This action will append these records directly to the live Firestore database. Unique Student serial numbers and household IDs will be generated automatically where required.</p>
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <button
                  onClick={() => setStep('mapping')}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Adjust Mappings
                </button>
                <button
                  onClick={handleExecuteImport}
                  className="bg-emerald-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm shadow flex items-center space-x-1.5"
                >
                  <span>Start Bulk Import</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: IMPORTING PROGRESS */}
          {step === 'importing' && (
            <div className="py-12 flex flex-col items-center justify-center space-y-6 text-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-slate-200 border-t-emerald-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileSpreadsheet size={20} className="text-emerald-600 animate-pulse" />
                </div>
              </div>
              <div className="space-y-2 max-w-sm">
                <h4 className="font-bold text-gray-800 text-lg">Writing to Firestore Database...</h4>
                <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                  <div 
                    className="bg-emerald-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${totalRecords > 0 ? (importedCount / totalRecords) * 100 : 0}%` }}
                  ></div>
                </div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                  Processing: {importedCount} / {totalRecords} records
                </p>
              </div>
            </div>
          )}

          {/* STEP 5: COMPLETED */}
          {step === 'completed' && (
            <div className="py-8 flex flex-col items-center justify-center space-y-5 text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 shadow-inner">
                <Check size={32} />
              </div>
              <div className="space-y-1 max-w-md">
                <h4 className="font-extrabold text-gray-900 text-xl">Import Completed!</h4>
                <p className="text-sm text-slate-500">
                  Successfully imported <strong>{importedCount}</strong> new summer camp household registrations.
                </p>
              </div>

              {importLogs.length > 0 && (
                <div className="w-full max-w-lg bg-red-50 border border-red-100 rounded-lg p-3 text-left space-y-1 max-h-32 overflow-y-auto">
                  <p className="text-xs font-bold text-red-800">Errors & Warnings:</p>
                  {importLogs.map((log, i) => (
                    <div key={i} className="text-[10px] text-red-600 flex items-center gap-1">
                      <span>•</span> {log}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm shadow"
              >
                Done & Refresh Dashboard
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
